import type { InputHTMLAttributes } from "react";
import { toLatinDigits } from "@/lib/i18n/date/digits";

export const NUMBER_INPUT_PATTERN = String.raw`[+\-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+\-]?[0-9]+)?`;

function normalizeDecimal(value: string) {
  return toLatinDigits(value).replace(/٫/g, ".").replace(/٬/g, "");
}

/** Numeric data stays ASCII; free text and passwords retain the user's script. */
export function getInputNormalizer(
  type?: string,
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"],
) {
  if (type === "number" || inputMode === "decimal") {
    return normalizeDecimal;
  }
  if (type === "tel" || inputMode === "numeric" || inputMode === "tel") {
    return toLatinDigits;
  }
  return undefined;
}

export type NumberConstraints = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "min" | "max" | "step" | "required"
>;

/** Retain native range/step validity after switching the visible control to text. */
export function syncNumberValidity(
  input: HTMLInputElement,
  { min, max, step, required }: NumberConstraints,
) {
  const numeric = input.ownerDocument.createElement("input");
  numeric.type = "number";
  numeric.required = required ?? false;
  if (min !== undefined) numeric.min = String(min);
  if (max !== undefined) numeric.max = String(max);
  if (step !== undefined) numeric.step = String(step);
  numeric.value = input.value;
  // Malformed non-empty text is covered by the visible input's pattern. The
  // native number setter sanitizes that text to empty, so do not report it as
  // a missing required value instead. Keep valid ranges and step restrictions.
  input.setCustomValidity(
    input.value && !numeric.value ? "" : numeric.validationMessage,
  );
}

export function normalizeInputValue<T>(
  value: T,
  normalize?: (value: string) => string,
) {
  return typeof value === "string" && normalize ? normalize(value) : value;
}
